/**
 * CollectionGridSection Component
 * 
 * Grid of curated collections with book cover collages
 */

import React from 'react';
import { View, StyleSheet, Pressable, Image, ActivityIndicator, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../../../components/ui';
import { CollectionWithBooks } from '../../../hooks/useExploreSections';

interface CollectionGridSectionProps {
  title?: string;
  subtitle?: string;
  collections: CollectionWithBooks[];
  icon?: keyof typeof Ionicons.glyphMap;
}

// Define collection styles globally
const getCollectionStyle = (id: string) => {
  switch (id) {
    case 'launch-your-big-idea':
      return { colors: ['#92400E', '#78350F'], icon: '🚀' };
    case 'master-your-mind':
      return { colors: ['#6B21A8', '#581C87'], icon: '🧠' };
    case 'build-better-habits':
      return { colors: ['#065F46', '#064E3B'], icon: '➕' };
    case 'find-your-focus':
      return { colors: ['#5B21B6', '#4C1D95'], icon: '🎯' };
    default:
      return { colors: ['#1E40AF', '#1E3A8A'], icon: '📚' };
  }
};

export const CollectionGridSection: React.FC<CollectionGridSectionProps> = ({
  title = 'Collections',
  subtitle = 'Curated book collections to help you grow',
  collections,
  icon = 'library',
}) => {
  const navigation = useNavigation();

  const handleCollectionPress = (collection: CollectionWithBooks) => {
    // Navigate to collection detail screen
    // @ts-ignore - Navigation types
    navigation.navigate('CollectionDetail', {
      collectionId: collection.id,
      collectionTitle: collection.title,
      collectionIcon: getCollectionStyle(collection.id).icon,
      bookIds: collection.bookIds,
    });
  };

  if (collections.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Ionicons name={icon} size={20} color={Theme.colors.primary} />
          <Text variant="h3" style={styles.title}>{title}</Text>
        </View>
        {subtitle && (
          <Text variant="caption" style={styles.subtitle}>
            {subtitle}
          </Text>
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {collections.map((collection) => (
          <CollectionCard
            key={collection.id}
            collection={collection}
            onPress={() => handleCollectionPress(collection)}
          />
        ))}
      </ScrollView>
    </View>
  );
};

interface CollectionCardProps {
  collection: CollectionWithBooks;
  onPress: () => void;
}

const CollectionCard: React.FC<CollectionCardProps> = ({ collection, onPress }) => {
  const style = getCollectionStyle(collection.id);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.card,
        pressed && styles.cardPressed,
      ]}
      onPress={onPress}
    >
      {/* Gradient Background */}
      <View style={[styles.gradientBg, { backgroundColor: style.colors[0] }]}>
        <View style={[styles.gradientOverlay, { backgroundColor: style.colors[1] }]} />
        
        {/* Decorative circles */}
        <View style={[styles.decorCircle, styles.decorCircle1]} />
        <View style={[styles.decorCircle, styles.decorCircle2]} />
        <View style={[styles.decorCircle, styles.decorCircle3]} />
        
        {/* Title */}
        <View style={styles.cardTitleContainer}>
          <Text style={styles.titleText} numberOfLines={2}>
            {collection.title}
          </Text>
        </View>
      </View>

      {/* Center Icon - positioned relative to card, not gradientBg */}
      <View style={styles.iconContainer}>
        <Text style={styles.iconText}>{style.icon}</Text>
      </View>

      {collection.loading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="small" color="#fff" />
        </View>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
    marginBottom: 4,
  },
  title: {
    color: 'rgb(0, 71, 89)',
  },
  subtitle: {
    marginTop: 2,
    color: 'rgba(0, 71, 89, 0.7)',
  },
  scrollView: {
    // No negative margin needed
  },
  scrollContent: {
    paddingLeft: Theme.spacing.lg,
    paddingRight: Theme.spacing.lg,
    gap: Theme.spacing.md,
  },
  card: {
    width: 140,
    height: 140,
    borderRadius: Theme.borderRadius.xl,
    overflow: 'hidden',
  },
  cardPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.98 }],
  },
  gradientBg: {
    flex: 1,
    paddingTop: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.sm,
    paddingBottom: Theme.spacing.sm,
    position: 'relative',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.7,
  },
  decorCircle: {
    position: 'absolute',
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  decorCircle1: {
    width: 35,
    height: 35,
    top: 10,
    right: 10,
  },
  decorCircle2: {
    width: 25,
    height: 25,
    bottom: 40,
    left: 10,
  },
  decorCircle3: {
    width: 18,
    height: 18,
    bottom: 15,
    right: 15,
  },
  cardTitleContainer: {
    position: 'relative',
    zIndex: 10,
  },
  titleText: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    fontWeight: '700',
  },
  iconContainer: {
    position: 'absolute',
    top: 68,
    left: 68,
    width: 40,
    height: 40,
    marginTop: -20,
    marginLeft: -20,
    zIndex: 5,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
    paddingRight: 2,
  },
  iconText: {
    fontSize: 36,
    opacity: 0.9,
    lineHeight: 36,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

